// ApiProvider — api suite root.
//
// Owns: the typed ApiRoutes wrapper (built on the axios instance + retry interceptor
// from lib/apiClient.ts), version-deviation tracking, and the imperative `applyAuth`
// callback the auth suite uses to bind the current access token to outgoing requests.

'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  ApiWrapper,
  BACKEND_CLIENT_VERSION,
  createApiRoutes,
  type ApiRoutes,
  type VersionDeviation,
} from '@cabal/shared/api-client';
import { FRONTEND_CLIENT_VERSION } from '@/config/frontend-client-version';
import { resolveBackendUrl } from '@/config/backend-url';
import { createApiTransport } from '@/lib/apiClient';
import { ensureFreshAccessToken } from '@/lib/authTokenFreshness';

interface ApplyAuthInput {
  accessToken?: string;
}

interface ApiContextValue {
  routes: ApiRoutes;
  applyAuth: (input: ApplyAuthInput) => void;
  backendClientVersion: string;
  frontEndVersion: string;
  deviation: VersionDeviation | null;
}

const ApiContext = createContext<ApiContextValue | null>(null);

const BACKEND_URL = resolveBackendUrl();

function createWrapper(accessToken?: string): ApiWrapper {
  return new ApiWrapper({
    backendUrl: BACKEND_URL,
    accessToken,
    frontEndVersion: FRONTEND_CLIENT_VERSION,
    transport: createApiTransport(),
    // Every generated-wrapper call resolves its bearer token through the
    // freshness precondition, so this path cannot send an expired token either
    // — the `applyAuth` snapshot alone would go stale the moment a tab was
    // backgrounded or the machine slept.
    authResolver: async () => (await ensureFreshAccessToken()) ?? undefined,
  });
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const wrapper = useMemo(() => createWrapper(accessToken), [accessToken]);
  const routes = useMemo<ApiRoutes>(() => createApiRoutes(wrapper), [wrapper]);
  const deviation = wrapper.getVersionDeviation();

  const applyAuth = useCallback((input: ApplyAuthInput) => {
    setAccessToken((currentValue) => {
      if (currentValue === input.accessToken) return currentValue;
      return input.accessToken;
    });
  }, []);

  const contextValue = useMemo<ApiContextValue>(
    () => ({
      routes,
      applyAuth,
      backendClientVersion: BACKEND_CLIENT_VERSION,
      frontEndVersion: FRONTEND_CLIENT_VERSION,
      deviation,
    }),
    [routes, applyAuth, deviation],
  );

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
}

export function useApiContext(): ApiContextValue {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('ApiProvider not mounted');
  return ctx;
}
